import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Spinner, Alert } from '@/components/ui';
import { TemplateCard } from '@/components/template';
import { useTemplates } from '@/hooks';
import { VideoCameraIcon } from '@heroicons/react/24/outline';

function SelectTemplateForEvaluation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // share_with param is preserved through template selection so athletes can
  // deep-link to evaluate a specific template and share with a specific coach.
  const shareWith = searchParams.get('share_with');
  const { data: templates, isLoading, error } = useTemplates();

  const handleSelectTemplate = (templateId: string) => {
    navigate(`/evaluate/${templateId}${shareWith ? '?share_with=' + shareWith : ''}`);
  };

  return (
    <div>
      <PageHeader
        title="Select a Template to Evaluate"
        description="Choose a template to compare your athlete's performance against"
        showBack
      />

      <div className="max-w-7xl mx-auto">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="mt-4 text-text-secondary">Loading templates...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="py-12">
            <Alert variant="error">
              Failed to load templates: {error.message}
            </Alert>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && templates && templates.length === 0 && (
          <div className="text-center py-12">
            <VideoCameraIcon className="mx-auto h-16 w-16 text-text-muted" />
            <h3 className="mt-4 text-lg font-medium text-text-primary">No templates available</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Create a template first before evaluating athlete performance.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/templates/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-sm-control text-text-inverse bg-accent hover:bg-accent-hover"
              >
                Open Coaching Studio
              </button>
            </div>
          </div>
        )}

        {/* Template Grid */}
        {!isLoading && !error && templates && templates.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-text-secondary">
                {templates.length} {templates.length === 1 ? 'template' : 'templates'} available
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEvaluate={handleSelectTemplate}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SelectTemplateForEvaluation;
